export type QuickResponseType =
  | 'see_proposed_correction'
  | 'not_accepted'
  | 'accepted'
  | 'requires_company_approval'
  | string;

/** OOXML / HTML track-changes author for Quick Response inserts (our-side, blue). */
export const QUICK_RESPONSE_TRACK_CHANGES_AUTHOR = 'Quick Response';

/** Authors whose track changes render as our-side (blue), not counterparty (red). */
export function isOurSideTrackChangesAuthor(author: string): boolean {
  const a = author.trim();
  return a === 'AI Suggestion' || a === 'User Edit' || a === QUICK_RESPONSE_TRACK_CHANGES_AUTHOR;
}

export interface QuickResponseOption {
  id: QuickResponseType;
  label: string;
  suffix: string;
}

export const MAX_CUSTOM_QUICK_RESPONSES = 20;
export const MAX_CUSTOM_QUICK_RESPONSE_LENGTH = 80;

export const QUICK_RESPONSE_OPTIONS: QuickResponseOption[] = [
  {
    id: 'see_proposed_correction',
    label: '[ראה תיקון מוצע]',
    suffix: ' [ראה תיקון מוצע]',
  },
  {
    id: 'not_accepted',
    label: '[לא מקובל]',
    suffix: ' [לא מקובל]',
  },
  {
    id: 'accepted',
    label: '[מקובל]',
    suffix: ' [מקובל]',
  },
  {
    id: 'requires_company_approval',
    label: '[דורש אישור חברה]',
    suffix: ' [דורש אישור חברה]',
  },
];

const BUILTIN_INNERS = new Set(
  QUICK_RESPONSE_OPTIONS.map((option) => option.label.slice(1, -1).toLowerCase()),
);

export function normalizeQuickResponseLabel(value: string): string {
  let text = value.trim();
  const wrappers: Array<[string, string]> = [
    ['[', ']'],
    ['״', '״'],
    ['"', '"'],
    ["'", "'"],
  ];
  for (const [left, right] of wrappers) {
    if (text.length >= 2 && text.startsWith(left) && text.endsWith(right)) {
      text = text.slice(left.length, -right.length).trim();
      break;
    }
  }
  return text;
}

export function sanitizeCustomQuickResponses(values: unknown): string[] {
  if (!Array.isArray(values)) return [];
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    if (typeof value !== 'string') continue;
    const label = normalizeQuickResponseLabel(value);
    if (!label || label.length > MAX_CUSTOM_QUICK_RESPONSE_LENGTH) continue;
    const key = label.toLowerCase();
    if (seen.has(key) || BUILTIN_INNERS.has(key)) continue;
    seen.add(key);
    result.push(label);
    if (result.length >= MAX_CUSTOM_QUICK_RESPONSES) break;
  }
  return result;
}

export function getCustomQuickResponsesFromMetadata(metadata: unknown): string[] {
  if (!metadata || typeof metadata !== 'object') return [];
  const prefs = (metadata as { preferences?: { custom_quick_responses?: unknown } }).preferences;
  return sanitizeCustomQuickResponses(prefs?.custom_quick_responses);
}

export function getQuickResponseIcon(id: string): string {
  if (id === 'not_accepted') return '✕';
  if (id === 'accepted') return '✓';
  if (id === 'requires_company_approval') return '!';
  if (id.startsWith('custom_')) return '•';
  return '↗';
}

export function getQuickResponseOptions(customLabels: string[] = []): QuickResponseOption[] {
  const extras = sanitizeCustomQuickResponses(customLabels).map((inner, index) => ({
    id: `custom_${index}`,
    label: `[${inner}]`,
    suffix: ` [${inner}]`,
  }));
  return [...QUICK_RESPONSE_OPTIONS, ...extras];
}

/** Strip one leftover QR suffix (same option) so a second click rebuilds a clean append. */
export function stripQuickResponseSuffix(
  baseText: string,
  type: QuickResponseType,
  options: QuickResponseOption[] = QUICK_RESPONSE_OPTIONS,
): string {
  const option = options.find((entry) => entry.id === type);
  let normalized = (baseText || '').replace(/\s+/g, ' ').trim();
  if (!option) return normalized;
  const suffixTrimmed = option.suffix.trim();
  if (suffixTrimmed && normalized.endsWith(suffixTrimmed)) {
    normalized = normalized.slice(0, -suffixTrimmed.length).replace(/\s+/g, ' ').trim();
  }
  return normalized;
}

export function buildQuickResponseText(
  baseText: string,
  type: QuickResponseType,
  options: QuickResponseOption[] = QUICK_RESPONSE_OPTIONS,
): string {
  const option = options.find((entry) => entry.id === type);
  if (!option) return baseText.replace(/\s+/g, ' ').trim();

  const normalized = stripQuickResponseSuffix(baseText, type, options);
  const suffixTrimmed = option.suffix.trim();
  if (!normalized) return suffixTrimmed;
  return `${normalized}${option.suffix}`;
}
